import React, { useState, useRef } from 'react';
import axios from 'axios';
import { createClient } from '@supabase/supabase-js';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Textarea } from '../../../components/ui/textarea';
import { toast } from 'sonner';
import { Loader2, Upload, X } from 'lucide-react';
import QualityTierPicker from './QualityTierPicker';
import GenerationProgress from './GenerationProgress';
import CreativePreviewCard from './CreativePreviewCard';

// Initialize Supabase client
const SUPABASE_URL = 'https://qrpgvseyuyjwyccfjnju.supabase.co';
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InFycGd2c2V5dXlqd3ljY2Zqbmp1Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzE0OTMwNTEsImV4cCI6MjA4NzA2OTA1MX0.WfVCx9pxKISoZqvYqkYhSfSdUHVgScbMdZgqr3U9PwQ';
const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INDUSTRIES = [
  { value: 'trades', label: 'Trades' },
  { value: 'home-services', label: 'Home Services' },
  { value: 'construction', label: 'Construction' },
  { value: 'landscaping', label: 'Landscaping' },
  { value: 'plumbing', label: 'Plumbing' },
  { value: 'electrical', label: 'Electrical' },
  { value: 'cleaning', label: 'Cleaning' },
  { value: 'other', label: 'Other' }
];

function BrandedModeForm({ onJobStart }) {
  const [formData, setFormData] = useState({
    brandName: '',
    industry: '',
    primaryColor: '#00d4aa',
    secondaryColor: '',
    logoUrl: '',
    headline: '',
    ctaText: '',
    targetAudience: '',
    qualityTier: 'standard',
    variantCount: 1
  });

  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentJobId, setCurrentJobId] = useState(null);
  const [generatedResult, setGeneratedResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleColorChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file is an image
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }

      // Validate file size (10MB limit)
      const maxSizeMB = 10;
      if (file.size > maxSizeMB * 1024 * 1024) {
        toast.error(`File must be smaller than ${maxSizeMB}MB`);
        return;
      }

      setUploadedFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setUploadPreview(e.target.result);
      };
      reader.readAsDataURL(file);

      // Automatically upload to Supabase
      uploadToSupabase(file);
    }
  };

  const uploadToSupabase = async (file) => {
    try {
      setIsUploadingImage(true);

      const timestamp = Date.now();
      const fileName = `${timestamp}-${file.name}`;
      const filePath = `reference-images/${fileName}`;

      // Upload file to Supabase Storage
      const { data, error: uploadError } = await supabase.storage
        .from('creatives')
        .upload(filePath, file, { upsert: true });

      if (uploadError) {
        throw uploadError;
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('creatives')
        .getPublicUrl(data.path);

      setUploadedImageUrl(publicUrl);
      toast.success('Reference image uploaded successfully');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload reference image');
      setUploadedFile(null);
      setUploadPreview(null);
      fileInputRef.current.value = '';
    } finally {
      setIsUploadingImage(false);
    }
  };

  const clearUpload = () => {
    setUploadedFile(null);
    setUploadPreview(null);
    setUploadedImageUrl(null);
    fileInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.brandName.trim()) {
      toast.error('Brand name is required');
      return;
    }
    if (!formData.industry) {
      toast.error('Industry is required');
      return;
    }
    if (!formData.headline.trim()) {
      toast.error('Headline is required');
      return;
    }
    if (!formData.ctaText.trim()) {
      toast.error('CTA text is required');
      return;
    }

    try {
      setIsGenerating(true);

      const requestBody = {
        brand_name: formData.brandName,
        industry: formData.industry,
        primary_color: formData.primaryColor,
        secondary_color: formData.secondaryColor || null,
        logo_url: formData.logoUrl || null,
        headline: formData.headline,
        cta_text: formData.ctaText,
        target_audience: formData.targetAudience || null,
        quality_tier: formData.qualityTier,
        input_image_url: uploadedImageUrl || null
      };

      const response = await axios.post(`${API}/creative/generate-branded`, requestBody);

      setCurrentJobId(response.data.job_id);
      setGeneratedResult(null);
      // Hand the job up to the page so the completion toast survives tab switches.
      onJobStart?.(response.data.job_id);
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error.response?.data?.error || 'Failed to start generation');
      setIsGenerating(false);
    }
  };

  const handleGenerationComplete = (result) => {
    setGeneratedResult(result);
    setCurrentJobId(null);
    setIsGenerating(false);
    // Terminal toast is fired by the page-level useCreativeJobPoll hook.
  };

  const handleGenerationError = (error) => {
    // Terminal toast is fired by the page-level useCreativeJobPoll hook.
    // Surface timeouts (which the hook doesn't toast) so the user isn't left hanging.
    if (error && /timeout/i.test(error)) {
      toast.error(error);
    }
    setCurrentJobId(null);
    setIsGenerating(false);
  };

  if (currentJobId) {
    return (
      <div className="max-w-2xl">
        <GenerationProgress
          jobId={currentJobId}
          qualityTier={formData.qualityTier}
          onComplete={handleGenerationComplete}
          onError={handleGenerationError}
        />
      </div>
    );
  }

  if (generatedResult) {
    // Handle both single image URL and array of URLs
    const imageUrl = generatedResult.imageUrl || (generatedResult.imageUrls?.[0] || '');

    return (
      <div className="space-y-6">
        <CreativePreviewCard
          imageUrl={imageUrl}
          mode="Branded"
          tier={formData.qualityTier}
          createdAt={new Date().toISOString()}
        />
        <Button
          onClick={() => setGeneratedResult(null)}
          variant="outline"
          className="border-white/10"
        >
          Generate Another
        </Button>
      </div>
    );
  }

  return (
    <Card className="card max-w-2xl">
      <CardContent className="p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Brand Name */}
          <div>
            <Label htmlFor="brandName" className="text-white mb-2">Brand Name *</Label>
            <Input
              id="brandName"
              name="brandName"
              value={formData.brandName}
              onChange={handleInputChange}
              placeholder="Your brand name"
              className="bg-white/5 border-white/10"
            />
          </div>

          {/* Industry */}
          <div>
            <Label htmlFor="industry" className="text-white mb-2">Industry *</Label>
            <Select value={formData.industry} onValueChange={(value) => setFormData(prev => ({ ...prev, industry: value }))}>
              <SelectTrigger className="bg-white/5 border-white/10">
                <SelectValue placeholder="Select an industry" />
              </SelectTrigger>
              <SelectContent className="bg-[#1a1a2e] border-white/10">
                {INDUSTRIES.map(ind => (
                  <SelectItem key={ind.value} value={ind.value}>{ind.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Colors */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="primaryColor" className="text-white mb-2">Primary Color *</Label>
              <div className="flex gap-2">
                <input
                  id="primaryColor"
                  name="primaryColor"
                  type="color"
                  value={formData.primaryColor}
                  onChange={handleColorChange}
                  className="w-12 h-10 rounded border border-white/10 cursor-pointer"
                />
                <Input
                  type="text"
                  value={formData.primaryColor}
                  onChange={handleColorChange}
                  name="primaryColor"
                  placeholder="#00d4aa"
                  className="flex-1 bg-white/5 border-white/10"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="secondaryColor" className="text-white mb-2">Secondary Color (Optional)</Label>
              <div className="flex gap-2">
                <input
                  id="secondaryColor"
                  name="secondaryColor"
                  type="color"
                  value={formData.secondaryColor}
                  onChange={handleColorChange}
                  className="w-12 h-10 rounded border border-white/10 cursor-pointer"
                />
                <Input
                  type="text"
                  value={formData.secondaryColor}
                  onChange={handleColorChange}
                  name="secondaryColor"
                  placeholder="#ffffff"
                  className="flex-1 bg-white/5 border-white/10"
                />
              </div>
            </div>
          </div>

          {/* Logo URL */}
          <div>
            <Label htmlFor="logoUrl" className="text-white mb-2">Logo URL (Optional)</Label>
            <Input
              id="logoUrl"
              name="logoUrl"
              value={formData.logoUrl}
              onChange={handleInputChange}
              placeholder="https://example.com/logo.png"
              className="bg-white/5 border-white/10"
            />
          </div>

          {/* Headline */}
          <div>
            <Label htmlFor="headline" className="text-white mb-2">Headline *</Label>
            <Input
              id="headline"
              name="headline"
              value={formData.headline}
              onChange={handleInputChange}
              placeholder="Your main headline"
              className="bg-white/5 border-white/10"
            />
          </div>

          {/* CTA Text */}
          <div>
            <Label htmlFor="ctaText" className="text-white mb-2">CTA Text *</Label>
            <Input
              id="ctaText"
              name="ctaText"
              value={formData.ctaText}
              onChange={handleInputChange}
              placeholder="Call Now / Get a Free Quote"
              className="bg-white/5 border-white/10"
            />
          </div>

          {/* Target Audience */}
          <div>
            <Label htmlFor="targetAudience" className="text-white mb-2">Target Audience (Optional)</Label>
            <Textarea
              id="targetAudience"
              name="targetAudience"
              value={formData.targetAudience}
              onChange={handleInputChange}
              placeholder="Describe your target audience..."
              className="bg-white/5 border-white/10 resize-none"
              rows={3}
            />
          </div>

          {/* Reference Product Image Upload */}
          <div>
            <Label className="text-white mb-2">Product / Reference Image (Optional)</Label>
            {uploadPreview ? (
              <div className={`relative inline-block rounded-lg border-2 p-2 ${uploadedImageUrl ? 'border-[#00d4aa]' : 'border-grey-400'}`}>
                <img src={uploadPreview} alt="Product preview" className="w-20 h-20 object-cover rounded" />
                <p className="text-white/70 text-xs mt-1 truncate max-w-20">
                  {uploadedFile?.name.substring(0, 15)}
                </p>
                <button
                  type="button"
                  onClick={clearUpload}
                  disabled={isUploadingImage}
                  className="absolute top-0 right-0 p-0.5 bg-red-500 rounded-full hover:bg-red-600 disabled:opacity-50"
                >
                  <X className="w-3 h-3 text-white" />
                </button>
                {isUploadingImage && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded">
                    <Loader2 className="w-4 h-4 text-white animate-spin" />
                  </div>
                )}
              </div>
            ) : (
              <div
                onClick={() => !isUploadingImage && fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                  isUploadingImage
                    ? 'border-white/20 bg-white/5 opacity-50'
                    : 'border-white/20 hover:border-white/40 bg-white/5'
                }`}
              >
                {isUploadingImage ? (
                  <>
                    <Loader2 className="w-8 h-8 text-[#00d4aa] mx-auto mb-2 animate-spin" />
                    <p className="text-white/60 text-sm">Uploading...</p>
                  </>
                ) : (
                  <>
                    <Upload className="w-8 h-8 text-white/40 mx-auto mb-2" />
                    <p className="text-white/60 text-sm">Click to upload or drag and drop</p>
                    <p className="text-white/40 text-xs">PNG, JPG up to 10MB</p>
                  </>
                )}
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileSelect}
              accept="image/*"
              disabled={isUploadingImage}
              className="hidden"
            />
          </div>

          {/* Quality Tier */}
          <div>
            <Label className="text-white mb-4 block">Quality Tier</Label>
            <QualityTierPicker
              value={formData.qualityTier}
              onChange={(tier) => setFormData(prev => ({ ...prev, qualityTier: tier }))}
              variantCount={formData.variantCount}
              onVariantCountChange={(count) => setFormData(prev => ({ ...prev, variantCount: count }))}
            />
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={isGenerating}
            className="w-full bg-[#00d4aa] hover:bg-[#00e6bb] text-black font-medium"
          >
            {isGenerating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              'Generate Creative'
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

export default BrandedModeForm;
